import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	type DialogProps,
	FormControlLabel,
	Grid2,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import {
	EMAIL_RECIPIENT_INITIAL_VALUES,
	type EmailRecipient,
	type ModalMode,
} from "../models/EmailRecipient";
import {
	deleteEmailRecipient,
	upsertEmailRecipient,
} from "../repositories/EmailRecipientsRepository";

type Props = {
	modal: ModalMode;
	recipient: EmailRecipient | null;
	onHide: (savedMessage?: string) => void;
};

export const UpsertDeleteRecipient = ({ modal, recipient, onHide }: Props) => {
	const maxWidth: DialogProps["maxWidth"] = "sm";
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const { control, reset, handleSubmit } = useForm<EmailRecipient>({
		defaultValues: EMAIL_RECIPIENT_INITIAL_VALUES,
	});

	useEffect(() => {
		if (modal === "") return;
		reset(
			modal === "new"
				? EMAIL_RECIPIENT_INITIAL_VALUES
				: (recipient ?? undefined),
		);
	}, [modal, recipient, reset]);

	const onSubmit: SubmitHandler<EmailRecipient> = async (data) => {
		setIsSubmitLoading(true);
		const resp =
			modal === "delete"
				? await deleteEmailRecipient(recipient?.RecipientId as number)
				: await upsertEmailRecipient({
						...data,
						RecipientId: recipient?.RecipientId ?? null,
					});
		setIsSubmitLoading(false);

		if (resp.ok) {
			onHide(
				modal === "delete"
					? "The address was deleted"
					: modal === "new"
						? "The address was added"
						: "The address was updated",
			);
		} else {
			setError(resp.message);
		}
	};

	const title = modal === "new" ? "NEW" : modal === "edit" ? "EDIT" : "DELETE";

	return (
		<>
			<MesfModal
				title={`${title} EMAIL RECIPIENT`}
				open={modal !== ""}
				maxWidth={maxWidth}
				handleClose={() => onHide()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						{modal === "delete" ? (
							<Grid2 container spacing={1}>
								<Grid2 size={12}>
									<Typography>
										Delete {recipient?.DisplayName} ({recipient?.EmailAddress})?
									</Typography>
								</Grid2>
								{!!recipient?.TemplateCount && (
									<Grid2 size={12}>
										<Typography color="error" sx={{ mt: 1 }}>
											{recipient.TemplateCount} template(s) still notify this
											address. Set it to inactive instead, or take it off those
											templates first.
										</Typography>
									</Grid2>
								)}
							</Grid2>
						) : (
							<Grid2 container spacing={1}>
								<Grid2 size={12}>
									<Controller
										name="DisplayName"
										control={control}
										rules={{ required: "Name is required" }}
										render={({
											field: { onChange, value },
											fieldState: { error },
										}) => (
											<TextField
												label="Name"
												variant="outlined"
												fullWidth
												margin="dense"
												autoComplete="off"
												onChange={onChange}
												value={value ?? ""}
												error={!!error}
												helperText={error?.message}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={12}>
									<Controller
										name="EmailAddress"
										control={control}
										rules={{ required: "Email address is required" }}
										render={({
											field: { onChange, value },
											fieldState: { error },
										}) => (
											<TextField
												label="Email Address"
												variant="outlined"
												fullWidth
												margin="dense"
												autoComplete="off"
												onChange={onChange}
												value={value ?? ""}
												error={!!error}
												helperText={error?.message ?? "One address at a time"}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={12}>
									<Controller
										name="IsActive"
										control={control}
										render={({ field: { onChange, value } }) => (
											<FormControlLabel
												control={
													<Switch
														checked={!!value}
														onChange={(e) => onChange(e.target.checked)}
													/>
												}
												label="Active"
											/>
										)}
									/>
								</Grid2>
							</Grid2>
						)}
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid2 justifyContent="flex-end" size={12} width={"100%"}>
							<Grid2 container justifyContent="flex-end" spacing={1}>
								<Grid2 size={{ md: 3, xs: 5 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={() => onHide()}
									>
										Cancel
									</Button>
								</Grid2>
								<Grid2 size={{ md: 3, xs: 5 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading}
										variant="contained"
										color={modal === "delete" ? "error" : "primary"}
										type="submit"
									>
										{modal === "delete" ? "Delete" : "Save"}
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
